/**
 * StepList — Reusable pipeline step progress list.
 * Used by all three module LoadingState views.
 */
import type { PipelineStep } from '@/types'

interface StepListProps {
  steps:      PipelineStep[]
  activeStep: string
}

export default function StepList({ steps, activeStep }: StepListProps) {
  const isQueued  = activeStep === 'queued' || activeStep === 'starting'
  const activeIdx = isQueued ? 0 : steps.findIndex(s => s.id === activeStep)
  const resolvedIdx = activeIdx >= 0 ? activeIdx : steps.length - 1

  return (
    <ol className="relative space-y-1" aria-label="Pipeline progress">
      {steps.map((s, i) => {
        const isDone    = i < resolvedIdx
        const isActive  = i === resolvedIdx
        // When queued, show step 0 as dim-pulsing to signal "about to start"
        const isInit    = isQueued && i === 0
        const isPending = !isDone && !isActive

        return (
          <li key={s.id} className="relative flex items-center gap-3 py-1">
            {/* Connector line (except last) */}
            {i < steps.length - 1 && (
              <span
                className={`step-connector ${isDone ? 'step-connector-done' : ''}`}
                aria-hidden="true"
              />
            )}

            {/* Step icon */}
            <span className={`relative z-10 flex-shrink-0 w-6 h-6 rounded-full flex items-center
              justify-center transition-all duration-300
              ${isDone    ? 'bg-emerald-500'                            : ''}
              ${isActive && !isInit ? 'bg-brand ring-4 ring-blue-100 ping-slow' : ''}
              ${isInit    ? 'bg-slate-200 ring-2 ring-slate-100'        : ''}
              ${isPending && !isInit ? 'bg-slate-100'                   : ''}
            `}>
              {isDone && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5"
                        strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              {isActive && !isInit && <span className="w-2 h-2 bg-white rounded-full" />}
              {isInit    && <span className="w-2 h-2 bg-slate-400 rounded-full animate-pulse" />}
              {isPending && !isInit && <span className="w-2 h-2 bg-slate-300 rounded-full" />}
            </span>

            {/* Step label */}
            <span className={`text-sm transition-colors duration-200
              ${isDone    ? 'text-slate-400 line-through decoration-slate-300' : ''}
              ${isActive && !isInit ? 'text-slate-900 font-medium'             : ''}
              ${isInit    ? 'text-slate-400 italic'                            : ''}
              ${isPending && !isInit ? 'text-slate-300'                        : ''}
            `}>
              {isInit ? `${s.label}\u2026` : s.label}
            </span>
          </li>
        )
      })}
    </ol>
  )
}
